import React, { useState } from 'react';
import { useTeam } from '../../../../shared/context/teamContext';
import { RefreshCw } from 'lucide-react';

// Text only: titles/main = #0f172a, sub/values = #475569
const TITLE = '#0f172a';
const SUB = '#475569';

const UsageDashboard = ({ usageData, planData, subscriptionData, selectedTeamId, onRefresh }) => {
  const { selectedTeam } = useTeam();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setRefreshing(false);
    }
  };

  const getDaysUntilReset = (resetDate) => {
    if (!resetDate) return null;
    try {
      const reset = new Date(resetDate);
      if (isNaN(reset.getTime())) return null;
      const diffDays = Math.ceil((reset - new Date()) / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch (e) {
      return null;
    }
  };

  const getUsagePeriod = () => {
    const isFreePlan = !subscriptionData?.plan || subscriptionData?.plan === 'free';
    const billingCycle = subscriptionData?.billingCycle || 'monthly';
    if (isFreePlan) {
      const createdAt = selectedTeam?.created_at || selectedTeam?.createdAt;
      if (createdAt) {
        const date = new Date(createdAt);
        const start = new Date(date.getFullYear(), date.getMonth(), 1);
        const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return `${start.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })} – ${end.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
    } else if (billingCycle === 'annual') {
      const periodStart = subscriptionData?.currentPeriodStart;
      const periodEnd = subscriptionData?.currentPeriodEnd;
      const now = new Date();
      if (periodStart && periodEnd) {
        const billingStart = new Date(periodStart);
        const billingEnd = new Date(periodEnd);
        const dayOfMonth = billingStart.getDate();
        const currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
        if (now < currentPeriodStart) currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
        const nextPeriodStart = new Date(currentPeriodStart);
        nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1);
        nextPeriodStart.setDate(nextPeriodStart.getDate() - 1);
        const actualPeriodEnd = nextPeriodStart > billingEnd ? billingEnd : nextPeriodStart;
        const actualPeriodStart = currentPeriodStart < billingStart ? billingStart : currentPeriodStart;
        return `${actualPeriodStart.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })} – ${actualPeriodEnd.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
    } else {
      const periodStart = subscriptionData?.currentPeriodStart;
      const periodEnd = subscriptionData?.currentPeriodEnd;
      if (periodStart && periodEnd) {
        const start = new Date(periodStart);
        const end = new Date(periodEnd);
        return `${start.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })} – ${end.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      }
    }
    return 'Current period';
  };

  const getCurrentPeriodEnd = () => {
    if (subscriptionData?.billingCycle !== 'annual') return null;
    const periodStart = subscriptionData?.currentPeriodStart;
    const periodEnd = subscriptionData?.currentPeriodEnd;
    const now = new Date();
    if (!periodStart || !periodEnd) return null;
    const billingStart = new Date(periodStart);
    const billingEnd = new Date(periodEnd);
    const dayOfMonth = billingStart.getDate();
    const currentPeriodStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
    if (now < currentPeriodStart) currentPeriodStart.setMonth(currentPeriodStart.getMonth() - 1);
    const nextPeriodStart = new Date(currentPeriodStart);
    nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1);
    nextPeriodStart.setDate(nextPeriodStart.getDate() - 1);
    return nextPeriodStart > billingEnd ? billingEnd : nextPeriodStart;
  };

  const currentPeriodEnd = getCurrentPeriodEnd();
  const creditResetDate =
    (subscriptionData?.billingCycle === 'annual' && currentPeriodEnd && currentPeriodEnd.toISOString()) ||
    usageData?.nextResetAt ||
    (usageData?.billingAnchorAt ? (() => {
      const anchor = new Date(usageData.billingAnchorAt);
      anchor.setMonth(anchor.getMonth() + 1);
      return anchor.toISOString();
    })() : null) ||
    (usageData?.lastResetAt ? (() => {
      const lastReset = new Date(usageData.lastResetAt);
      lastReset.setMonth(lastReset.getMonth() + 1);
      return lastReset.toISOString();
    })() : null) ||
    (subscriptionData?.currentPeriodEnd ? new Date(subscriptionData.currentPeriodEnd).toISOString() : null);
  const daysUntilReset = getDaysUntilReset(creditResetDate);

  const teamUsage = {
    teamMembers: Array.isArray(selectedTeam?.user) ? selectedTeam.user.length : 0,
    websites: Array.isArray(selectedTeam?.websites) ? selectedTeam.websites.length : 0,
    smartContracts: Array.isArray(selectedTeam?.smartContracts) ? selectedTeam.smartContracts.length : 0,
    campaignLinks: Array.isArray(selectedTeam?.utm) ? selectedTeam.utm.length : 0,
    baseCredits: usageData?.baseCredits ?? 0,
    apiCalls: usageData?.apiCalls ?? 0,
    addonCredits: usageData?.addonCredits ?? 0,
  };

  const planLimits = { ...(subscriptionData?.limits || {}), ...(planData?.limits || {}) };

  const formatLimit = (limit) =>
    limit === null || limit === undefined ? 'Unlimited' : limit.toLocaleString();

  const rows = [
    { section: 'Project usage' },
    { label: 'Team members', used: teamUsage.teamMembers, limit: planLimits.teamMembers, reset: false },
    { label: 'Websites', used: teamUsage.websites, limit: planLimits.websites, reset: false },
    { label: 'Smart contracts', used: teamUsage.smartContracts, limit: planLimits.smartContracts, reset: false },
    { label: 'Campaign links', used: teamUsage.campaignLinks, limit: planLimits.campaignLinks, reset: false },
    { section: 'Credits' },
    { label: 'Base credits', used: teamUsage.baseCredits, limit: planLimits?.baseCredits ?? 0, reset: true },
    { label: 'API calls', used: teamUsage.apiCalls, limit: planLimits?.apiCalls ?? 0, reset: true },
    { section: 'Add-on' },
    { label: 'Wallet enhancement credits', used: teamUsage.addonCredits, limit: planLimits?.addonCredits ?? null, reset: false },
  ];

  return (
    <div className="w-full" style={{ fontFamily: "'Raleway', sans-serif" }}>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: TITLE }}>Usage</h1>
          <p className="text-base mt-1" style={{ color: SUB }}>{getUsagePeriod()}</p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 disabled:opacity-60 transition-colors"
          style={{ color: TITLE }}
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th className="text-left py-3 pr-6 text-sm font-semibold" style={{ color: TITLE }}>Resource</th>
              <th className="text-center py-3 px-4 text-sm font-semibold tabular-nums" style={{ color: TITLE }}>Used</th>
              <th className="text-center py-3 px-4 text-sm font-semibold tabular-nums" style={{ color: TITLE }}>Limit</th>
              <th className="text-center w-40 py-3 pl-4 text-sm font-semibold" style={{ color: TITLE }}>Remaining</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              if (row.section) {
                return (
                  <tr key={idx}>
                    <td colSpan={4} className="pt-6 pb-2 text-sm font-semibold" style={{ color: TITLE }}>
                      {row.section}
                    </td>
                  </tr>
                );
              }
              const hasLimit = row.limit !== null && row.limit !== undefined;
              const limitNum = hasLimit && row.limit > 0 ? row.limit : null;
              const pct = limitNum ? Math.min(100, (row.used / limitNum) * 100) : 0;
              const remaining = limitNum != null ? Math.max(0, limitNum - row.used) : null;
              return (
                <tr key={idx} className="border-t border-gray-200">
                  <td className="py-4 pr-6 text-base" style={{ color: TITLE }}>
                    {row.label}
                    {row.reset && daysUntilReset != null && (
                      <span className="font-normal ml-1" style={{ color: SUB }}>
                        · Resets in {daysUntilReset} {daysUntilReset === 1 ? 'day' : 'days'}
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center text-base tabular-nums" style={{ color: SUB }}>{row.used.toLocaleString()}</td>
                  <td className="py-4 px-4 text-center text-base tabular-nums" style={{ color: SUB }}>{formatLimit(row.limit)}</td>
                  <td className="py-4 pl-4 text-center">
                    {row.reset ? (
                      <span className="text-sm tabular-nums" style={{ color: SUB }}>
                        {remaining != null ? `${remaining.toLocaleString()} remaining` : 'Unlimited'}
                      </span>
                    ) : limitNum != null ? (
                      /* Project usage & Add-on: progress bar */
                      <div className="space-y-1">
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full transition-[width]"
                            style={{ width: `${pct}%`, backgroundColor: SUB }}
                          />
                        </div>
                        <div className="flex justify-between text-xs tabular-nums" style={{ color: SUB }}>
                          <span>{remaining != null ? `${remaining.toLocaleString()} left` : '—'}</span>
                          <span>{pct.toFixed(0)}%</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm" style={{ color: SUB }}>Unlimited</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsageDashboard;
